import React, { useEffect, useState } from "react";
import { Box, Dialog, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SaveOutlined, TrackChanges } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import CustomTextField from "../../common/CustomTextField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { FieldLabel } from "../../../utils/Utility";
import { useModalStyles } from "../../common/useModalStyles";
import {
  MONTH_OPTIONS,
  SalesGoalFormValues,
  SalesGoalRecord,
  isCollectionTargetAllowed,
  isActualCollectedAmtAllowed,
  validateCollectionTarget,
  validateOptionalAchievementPct,
  validateOptionalActualCollectedAmt,
  validateSalesGoalYear,
} from "./isrTypes";

interface SalesGoalPopupProps {
  open: boolean;
  salesGoal?: SalesGoalRecord | null;
  onClose: () => void;
  onSubmitForm: (data: SalesGoalFormValues) => void;
}

const emptyValues: SalesGoalFormValues = {
  year: "",
  month: "",
  invoiceYear: "",
  collectionTarget: "",
  actualCollectedAmt: "",
  achievementPct: "",
};

const SalesGoalPopup: React.FC<SalesGoalPopupProps> = ({ open, salesGoal, onClose, onSubmitForm }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { control, handleSubmit, reset, formState: { errors } } = useForm<SalesGoalFormValues>({
    defaultValues: emptyValues,
  });
  const [isEdit, setIsEdit] = useState(false);

  const title = isEdit ? "Edit Sales Goal" : "Add Sales Goal";
  const subtitle = isEdit
    ? "Update collection target; actual collected and achievement are optional."
    : "Enter the monthly collection target details.";

  useEffect(() => {
    if (!open) {
      return;
    }
    setIsEdit(!!salesGoal);
    if (salesGoal) {
      reset({
        year: salesGoal.year,
        month: salesGoal.month,
        invoiceYear: salesGoal.invoiceYear,
        collectionTarget: salesGoal.collectionTarget,
        actualCollectedAmt: salesGoal.actualCollectedAmt,
        achievementPct: salesGoal.achievementPct,
      });
      return;
    }
    reset(emptyValues);
  }, [open, salesGoal, reset]);

  const handleDialogClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmitForm)}
        sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <TrackChanges sx={{ fontSize: 20, color: tokens.accent }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldLabel label="Payment Year" required tokens={tokens} />
              <Box sx={inputFieldSx(!!errors.year)}>
                <CustomTextField
                  control={control}
                  controllerName="year"
                  label=""
                  size="small"
                  isNumberOnly
                  maxLength={4}
                  rules={{
                    required: "Payment Year is required.",
                    validate: (value: string) => validateSalesGoalYear(value, "Payment Year"),
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Payment Month" required tokens={tokens} />
              <Box sx={dropdownWrapSx(!!errors.month)}>
                <CustomDropdownField
                  name="month"
                  control={control}
                  options={MONTH_OPTIONS}
                  label=""
                  size="small"
                  sortOptions={false}
                  rules={{ required: "Payment Month is required." }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Invoice Year" required tokens={tokens} />
              <Box sx={inputFieldSx(!!errors.invoiceYear)}>
                <CustomTextField
                  control={control}
                  controllerName="invoiceYear"
                  label=""
                  size="small"
                  isNumberOnly
                  maxLength={4}
                  rules={{
                    required: "Invoice Year is required.",
                    validate: (value: string) => validateSalesGoalYear(value, "Invoice Year"),
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Collection Target" required tokens={tokens} />
              <Box sx={inputFieldSx(!!errors.collectionTarget)}>
                <CustomCurrencyField
                  id="collectionTarget"
                  controllerName="collectionTarget"
                  control={control}
                  label=""
                  size="small"
                  isAllowed={isCollectionTargetAllowed}
                  rules={{
                    required: "Collection Target is required.",
                    validate: validateCollectionTarget,
                  }}
                />
              </Box>
            </Grid>
            {isEdit && (
              <>
                <Grid item xs={12}>
                  <FieldLabel label="Actual Collected Amt" tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.actualCollectedAmt)}>
                    <CustomCurrencyField
                      id="actualCollectedAmt"
                      controllerName="actualCollectedAmt"
                      control={control}
                      label=""
                      size="small"
                      isAllowed={isActualCollectedAmtAllowed}
                      rules={{
                        validate: validateOptionalActualCollectedAmt,
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel label="Achievement %" tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.achievementPct)}>
                    <CustomTextField
                      control={control}
                      controllerName="achievementPct"
                      label=""
                      size="small"
                      rules={{
                        validate: validateOptionalAchievementPct,
                      }}
                    />
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexDirection: { xs: "column", sm: "row" } }}>
            <Box
              component="button"
              type="submit"
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "none",
                cursor: "pointer",
                color: "#fff",
              }}
            >
              <SaveOutlined sx={{ fontSize: "1.1rem" }} />
              {isEdit ? "Update Goal" : "Save Goal"}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleDialogClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SalesGoalPopup;
